/**
 * 本文中のNGワードマーキングに関する状態。
 *
 *  - 表示ON/OFF(全体・レベル別): 審査者ごとに好みが違い、商品を切り替えるたびに
 *    リセットされると1日に何百回も押し直すことになるため localStorage に永続化する。
 *  - hovered / pinned: 詳細ペインに何を出すか。位置は固定で、内容だけが切り替わる
 *    (マウス位置に追従するポップオーバーにはしない。本文に被って前後の文脈が
 *     読めなくなるのを避けるため)。
 */
import { defineStore } from 'pinia'
import type { NgWordMatch } from '@/types/api'
import type { NgLevelBucket } from '@/utils/ngHighlight'

const STORAGE_KEY = 'salmon.ngMark'

// 閉じるまでの遅延タイマー(リアクティブにする必要がないためstate外に置く)
let closeTimer: ReturnType<typeof setTimeout> | null = null
function cancelCloseTimer() {
  if (closeTimer !== null) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
}

interface Persisted {
  enabled: boolean
  levels: Record<NgLevelBucket, boolean>
}

function loadPersisted(): Persisted {
  const fallback: Persisted = { enabled: true, levels: { 9: true, 7: true, 5: true } }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<Persisted>
    return {
      enabled: parsed.enabled ?? fallback.enabled,
      levels: { ...fallback.levels, ...(parsed.levels ?? {}) },
    }
  } catch {
    return fallback
  }
}

/** フローティング表示の基準位置(ホバーしたマークの画面上の矩形) */
export interface NgAnchorRect {
  left: number
  right: number
  top: number
  bottom: number
}

interface NgMarkState extends Persisted {
  /** 現在の商品のNGワード判定結果。リスト(NgWordPanel)と本文マーキングで共有する */
  ngWords: NgWordMatch[]
  /** ホバー中のマークのヒット一覧(マウスが外れたらnullに戻る) */
  hovered: NgWordMatch[] | null
  /** クリックで固定したヒット一覧。固定中はホバーで内容が変わらない */
  pinned: NgWordMatch[] | null
  /** フローティングパネルの表示基準位置 */
  anchor: NgAnchorRect | null
}

export const useNgMarkStore = defineStore('ngMark', {
  state: (): NgMarkState => ({
    ...loadPersisted(),
    ngWords: [],
    hovered: null,
    pinned: null,
    anchor: null,
  }),

  getters: {
    /** 実際にマークを描画する対象レベル。全体OFFなら空集合 */
    activeLevels(state): Set<NgLevelBucket> {
      const set = new Set<NgLevelBucket>()
      if (!state.enabled) return set
      for (const lv of [9, 7, 5] as NgLevelBucket[]) {
        if (state.levels[lv]) set.add(lv)
      }
      return set
    },
    /** 詳細ペインに表示する内容(固定が優先) */
    detailHits(state): NgWordMatch[] | null {
      return state.pinned ?? state.hovered
    },
    /** レベル別チェックが一部だけONか(親チェックボックスの中間状態に使う) */
    isPartial(state): boolean {
      const on = ([9, 7, 5] as NgLevelBucket[]).filter((lv) => state.levels[lv]).length
      return on > 0 && on < 3
    },
  },

  actions: {
    persist() {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ enabled: this.enabled, levels: this.levels }),
        )
      } catch {
        // プライベートブラウジング等で書き込めない場合は諦める(動作には影響しない)
      }
    },
    setEnabled(v: boolean) {
      this.enabled = v
      // 全体をONにしたのにレベルが全部OFFだと何も出ず不可解なので、その場合は全部ONに戻す
      if (v && !([9, 7, 5] as NgLevelBucket[]).some((lv) => this.levels[lv])) {
        this.levels = { 9: true, 7: true, 5: true }
      }
      this.persist()
    },
    setLevel(level: NgLevelBucket, v: boolean) {
      this.levels[level] = v
      // 1つでもONなら全体もONとみなす(親チェックボックスとの整合)
      if (v) this.enabled = true
      this.persist()
    },
    toggleEnabled() {
      this.setEnabled(!this.enabled)
    },
    /** 判定結果を差し替える。商品が切り替わったら詳細ペインの内容もリセットする */
    setNgWords(words: NgWordMatch[] | null | undefined) {
      this.ngWords = words ?? []
      this.hovered = null
      this.pinned = null
      this.anchor = null
    },
    /**
     * ホバー中のヒットを表示する。フローティングパネルはマークの真下に出すため、
     * マーク要素の画面上の位置も一緒に受け取る。
     */
    setHovered(hits: NgWordMatch[] | null, anchor?: NgAnchorRect | null) {
      this.hovered = hits
      if (hits && anchor) this.anchor = anchor
      if (!hits && !this.pinned) this.anchor = null
    },
    togglePinned(hits: NgWordMatch[], anchor?: NgAnchorRect | null) {
      const same =
        this.pinned &&
        this.pinned.length === hits.length &&
        this.pinned.every((p, i) => p.word === hits[i].word)
      this.pinned = same ? null : hits
      if (!same && anchor) this.anchor = anchor
    },
    /**
     * パネルの閉じ方には遅延を入れる。マークからパネルへカーソルを移す途中で
     * 消えてしまうと、参考リンクを踏めないため。
     */
    scheduleClose() {
      cancelCloseTimer()
      closeTimer = setTimeout(() => {
        closeTimer = null
        if (!this.pinned) this.setHovered(null)
      }, 180)
    },
    cancelClose() {
      cancelCloseTimer()
    },
    clear() {
      cancelCloseTimer()
      this.hovered = null
      this.pinned = null
      this.anchor = null
    },
  },
})
